/**
 * **"A fresh MyRave just dropped" — inside the phone app** (the user, 2026-10-30: "make the user update the app inside
 * it if a new version is available, both on Android and Apple"). The page asks its own host for the newest phone
 * build (`/app-update.json`, `worker/installLinks.ts` `appUpdateResponse`) once per start and again when it comes
 * back after `RECHECK_MS`; when this phone's build (the `(73)` its user agent carries) is older, a sheet offers
 * Update — Android the newest APK (the phone downloads and installs it), iPhone TestFlight — or Not now, which
 * leaves that build alone for `SNOOZE_MS`. A newer build asks again. Browsers: never.
 * In the raver's words, never a version number (the user, 2026-10-30: "the update note needs to be in raver language").
 *
 * **The Android APK updates INSIDE the app** (the 0.14.74 walk: "it links the user to Expo; I wanted the full install
 * wizard in the app"): Update asks the shell (`installUpdateInApp`) — it downloads the APK with a bar here, then
 * Android's own "Update this app?" screen. A shell that does not answer (an older build, Play, the iPhone) gets the
 * page move as before — TestFlight on the iPhone; a failed download says why and offers the link.
 *
 * **First, and again at every open on Android** (the 0.14.82 walk: "first I saw the 2 raves together and then the
 * update — the update notice should come first … I skipped the update and on the next open it should ask again until
 * the user is updating, now it's asking only once"). While the check is out or the sheet is up, the other start-up
 * pop-ups (`SameDates`, a rave's What's new) wait for `useAppUpdateSettled()`. On Android "Not now" lasts only until
 * the next open — the app started again, or brought back after `REOPEN_MS` away; the iPhone keeps its day.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { readNativeApp } from '@myrave/shared';
import { Button, Dialog } from '@/ui';
import { useT } from '@/i18n';
import { readJson, writeJson } from '@/lib/storage';
import { inNativeApp, installUpdateInApp } from '@/lib/nativeBridge';
import './AppUpdate.css';

export interface PhoneLatest { version: string; build: number; url: string }
export interface AppUpdateAnswer { android: PhoneLatest | null; ios: PhoneLatest | null }

export const SNOOZE_MS = 24 * 60 * 60_000;
export const RECHECK_MS = 6 * 60 * 60_000;
/** Android: away this long, coming back counts as opening the app again — "Not now" is asked again. */
export const REOPEN_MS = 60_000;
/** No answer within this, the other pop-ups stop waiting (no signal must not hold them for ever). */
export const CHECK_WAIT_MS = 4_000;
const LATER = 'app-update-later';

/** Android's "Not now", for this run of the app only. */
const skippedThisRun = new Set<number>();

/*
 * The start-up gate: 'checking' until the answer (or `CHECK_WAIT_MS`), 'offer' while the sheet is up, then 'clear'.
 * Outside the phone app it is always clear.
 */
type Gate = 'checking' | 'offer' | 'clear';
let gate: Gate = 'checking';
const listeners = new Set<() => void>();
function setGate(next: Gate) {
  if (gate === next) return;
  gate = next;
  for (const l of listeners) l();
}
/** For the tests: a fresh start of the app. */
export function resetAppUpdateGate() { gate = 'checking'; skippedThisRun.clear(); for (const l of listeners) l(); }

/** True once the update check has answered and no update sheet is up — the other start-up pop-ups wait for it. */
export function useAppUpdateSettled(): boolean {
  const g = useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => gate,
    () => 'clear' as Gate,
  );
  return g === 'clear' || !inNativeApp();
}

/** The newer build this phone should move to, or null. Exported for the test. */
export function updateFor(userAgent: string, answer: AppUpdateAnswer | null, now = Date.now()): PhoneLatest | null {
  const app = readNativeApp(userAgent);
  if (!app || !answer) return null;
  const latest = app.os === 'ios' ? answer.ios : app.os === 'android' ? answer.android : null;
  const mine = Number(app.build);
  if (!latest || !Number.isInteger(mine) || mine <= 0 || latest.build <= mine) return null;
  // Android asks at every open until it is updated: "Not now" holds for this run only.
  if (app.os === 'android') return skippedThisRun.has(latest.build) ? null : latest;
  const later = readJson<{ build?: number; until?: number } | null>(null, LATER, null);
  if (later && later.build === latest.build && (later.until ?? 0) > now) return null;
  return latest;
}

export function AppUpdate() {
  const tr = useT();
  const [offer, setOffer] = useState<PhoneLatest | null>(null);
  const last = useRef<AppUpdateAnswer | null>(null);
  useEffect(() => {
    if (!inNativeApp()) { setGate('clear'); return undefined; }
    let asked = 0;
    let hiddenAt = 0;
    let live = true;
    const show = (answer: AppUpdateAnswer | null) => {
      const next = updateFor(navigator.userAgent, answer);
      setOffer(next);
      setGate(next ? 'offer' : 'clear');
    };
    const ask = () => {
      if (Date.now() - asked < RECHECK_MS) return;
      asked = Date.now();
      void fetch('/app-update.json', { cache: 'no-store' })
        .then((r) => (r.ok ? r.json() as Promise<AppUpdateAnswer> : null))
        .then((answer) => { if (!live) return; last.current = answer; show(answer); })
        .catch(() => { if (live && gate === 'checking') setGate('clear'); /* no signal: ask next time */ });
    };
    const waited = setTimeout(() => { if (gate === 'checking') setGate('clear'); }, CHECK_WAIT_MS);
    ask();
    const visible = () => {
      if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
      // Android: back after a while is opening the app again — "Not now" is over, the offer comes back.
      if (hiddenAt && Date.now() - hiddenAt >= REOPEN_MS && readNativeApp(navigator.userAgent)?.os === 'android') {
        skippedThisRun.clear();
        if (last.current) show(last.current);
      }
      hiddenAt = 0;
      ask();
    };
    document.addEventListener('visibilitychange', visible);
    return () => { live = false; clearTimeout(waited); document.removeEventListener('visibilitychange', visible); };
  }, []);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [state, setState] = useState<'offer' | 'downloading' | 'ready' | { error: string }>('offer');
  if (!offer) return null;
  const update = async () => {
    setState('downloading');
    const got = await installUpdateInApp(offer.url, (done, total) => setProgress({ done, total }));
    if (got === 'unanswered') { window.location.assign(offer.url); return; }
    setState(got);
  };
  const later = () => {
    if (readNativeApp(navigator.userAgent)?.os === 'android') skippedThisRun.add(offer.build);
    else writeJson(null, LATER, { build: offer.build, until: Date.now() + SNOOZE_MS });
    setOffer(null);
    setGate('clear');
  };
  return (
    <Dialog
      title={tr('A fresh MyRave just dropped')}
      onClose={later}
      centered
      footer={(
        <>
          <Button variant="quiet" onClick={later}>{state === 'ready' ? tr('Close') : tr('Not now')}</Button>
          {state === 'offer' && <Button onClick={() => { void update(); }}>{tr('Update')}</Button>}
          {typeof state === 'object' && <Button onClick={() => { window.location.assign(offer.url); }}>{tr('Download it instead')}</Button>}
        </>
      )}
    >
      {state === 'offer' && <p>{tr("New features and fixes are waiting. Update so you don't miss a beat.")}</p>}
      {state === 'downloading' && (() => {
        const pct = progress && progress.total > 0 ? Math.min(100, Math.round((progress.done / progress.total) * 100)) : null;
        return (
          <>
            <p role="status">{pct === null ? tr('Downloading the update…') : tr('Downloading the update… {pct}%', { pct })}</p>
            <progress className="app-update-bar" max={100} {...(pct === null ? {} : { value: pct })} aria-label={tr('Downloading the update…')} />
          </>
        );
      })()}
      {state === 'ready' && <p role="status">{tr('Tap Update on Android’s screen to finish.')}</p>}
      {typeof state === 'object' && <p role="alert">{tr('The update did not download ({reason}).', { reason: state.error })}</p>}
    </Dialog>
  );
}
